<template>
  <div class="n-input" :class="{ 'has-error': errorMessage }">
    <div class="toggle__container">
      <div class="toggle__input-container">
        <input
          type="checkbox"
          v-model="value"
          :name="name"
          :id="inputId"
          :disabled="disabled"
          @change="emit('change', value)"
        />
        <div class="toggle" @click="handleToggleClick">
          <div class="toggle-inner"></div>
        </div>
      </div>
      <label :for="inputId" :class="{ 'sr-only': hideLabel }">{{ label }} </label>
    </div>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
  </div>
</template>

<script lang="ts" setup>
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';
import { useVModel } from '@vueuse/core';

defineOptions({
  name: 'n-toggle',
});

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  id: {
    type: String,
  },
  errorMessage: {
    type: String,
  },
  name: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
});

const { inputId } = useIdGenerator(props.id);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'change', value: boolean): void;
}>();
const value = useVModel(props, 'modelValue', emit);

function handleToggleClick() {
  if (!props.disabled) {
    emit('update:modelValue', !value.value);
    emit('change', !value.value);
  }
}
</script>

<style lang="scss" scoped>
@use '../../../styles/accessibility' as *;
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;

.n-input {
  .toggle__container {
    display: flex;
    align-items: center;
    label {
      margin: 0;
    }
  }
  .toggle__input-container {
    position: relative;
    margin-right: 0.5rem;
    .toggle {
      width: 3rem;
      height: 1.5rem;
      position: relative;
      display: block;
      background: colourScheme('grey', 'medium');
      border-radius: 24px;
      position: relative;
      cursor: pointer;
      .toggle-inner {
        display: block;
        position: absolute;
        top: 0.25rem;
        left: 0.25rem;
        width: 1rem;
        height: 1rem;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        border-radius: 50%;
        background: colourScheme('light');
        -webkit-transition: all 0.25s ease-out;
        transition: all 0.25s ease-out;
      }
    }
    input[type='checkbox'] {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      border: 0;
      &:checked {
        & + .toggle {
          background: colourScheme('deep-sea');
          .toggle-inner {
            left: 1.625rem;
          }
        }
      }
      &:disabled {
        & + .toggle {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }
  }
}
</style>
